import { createSlice } from "@reduxjs/toolkit";
import { pushDynamicTab } from "../utils/dynamicTabs";

const SLICE_VERSION = "1.0";

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

export const initialState = {
  _version: SLICE_VERSION,
  defaultTabs: [
    { id: "pipeline", name: "Sourcing Pipeline", contentType: "pipeline" },
    { id: "reporting", name: "Reporting", contentType: "reporting" },
  ],
  dynamicTabs: [],
  activeTab: 0,
  tabHistory: [],
};

const carrierSourcingSlice = createSlice({
  name: "carrierSourcing",
  initialState,
  reducers: {
    addTab: (state, action) => {
      const tabDetails = action.payload;
      if (!tabDetails) return;

      if (tabDetails.key) {
        const all = getAllTabs(state);
        const existingIndex = all.findIndex(
          (t) =>
            t.key === tabDetails.key && t.contentType === tabDetails.contentType
        );
        if (existingIndex >= 0) {
          const prevActiveId = all[state.activeTab]?.id;
          pushTabHistory(state, prevActiveId);
          if (existingIndex >= state.defaultTabs.length) {
            const dynIdx = existingIndex - state.defaultTabs.length;
            const current = state.dynamicTabs[dynIdx];
            state.dynamicTabs[dynIdx] = {
              ...current,
              ...(tabDetails.contentType === "editCarrier" ||
              tabDetails.contentType === "newRateRequest"
                ? tabDetails
                : {}),
              id: current.id,
              name: tabDetails.name || current.name,
            };
          }
          state.activeTab = existingIndex;
          return;
        }
      }

      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(state, tabDetails, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.defaultTabs.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

      if (!wasActive) {
        if (state.activeTab > tabIndex) state.activeTab -= 1;
        return;
      }

      const historyId = [...state.tabHistory].reverse().find((id) =>
        getAllTabs(state).some((t) => t.id === id)
      );
      if (historyId) {
        const idx = getAllTabs(state).findIndex((t) => t.id === historyId);
        state.activeTab = idx >= 0 ? idx : 0;
        state.tabHistory = state.tabHistory.filter((id) => id !== historyId);
      } else {
        state.activeTab = 0;
      }
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      const all = getAllTabs(state);
      if (index < 0 || index >= all.length) return;
      const prevActiveId = all[state.activeTab]?.id;
      pushTabHistory(state, prevActiveId);
      state.activeTab = index;
    },

    resetTabs: () => initialState,
  },
});

export const { addTab, removeTab, setActiveTab, resetTabs } =
  carrierSourcingSlice.actions;
export default carrierSourcingSlice.reducer;
